import React, { useState, useEffect, useCallback } from 'react';

const DiagnosisApp = () => {
  const [questions, setQuestions] = useState([]);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  // const [result, setResult] = useState(null);
  const [resultMessages, setResultMessages] = useState({});
  const [displayMessage, setDisplayMessage] = useState(null);  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 結果計算のトリガーとなる状態を追加
  // const [shouldCalculate, setShouldCalculate] = useState(false);

  // 質問と結果データの取得
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 質問データを取得
        const questionsResponse = await fetch(diagnosisAppData.questionsUrl);
        if (!questionsResponse.ok) {
          throw new Error('質問データの取得に失敗しました');
        }
        const questionsData = await questionsResponse.json();
        setQuestions(questionsData);

        // 回答データを取得
        const answersResponse = await fetch(diagnosisAppData.answersUrl);
        if (!answersResponse.ok) {
          throw new Error('回答データの取得に失敗しました');
        }
        const answersData = await answersResponse.json();
        setResultMessages(answersData);

      } catch (err) {
        console.error('データ取得エラー:', err);
        setError(`データの読み込みに失敗しました: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // GA4イベントを送信する関数
  const sendGAEvent = (questionId, answer) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'quiz_answer', {
        question_id: questionId,
        answer: answer,
        page_title: document.title,
        group_index: currentGroupIndex + 1
      });
    } else {
      console.warn('gtag is not defined');
    }
  };  

  // 回答の処理
  const handleAnswer = useCallback((questionId, answer) => {
    // GA4イベントを送信
    sendGAEvent(questionId, answer ? 'Yes' : 'No');

    setUserAnswers(prev => {
      // boolean値であることを保証
      const newAnswers = {
        ...prev,
        [questionId]: Boolean(answer)
      };

      return newAnswers;
    });
  }, [sendGAEvent]);

  // 次のグループへ進む
  const nextGroup = useCallback(() => {
    if (currentGroupIndex < questions.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const resultKey = computeResult(questions, userAnswers);
      if (resultKey) {
        handleResult(resultKey);
      }
    }
  }, [currentGroupIndex, questions.length, userAnswers]);

  // 結果計算ロジックを純粋な関数として定義
  const computeResult = (questionsData, answersData) => {
    if (!questionsData.length) return null;

    // グループごとのYes回答をカウント
    const groupCounts = questionsData.reduce((acc, group) => {
      const yesCount = group.questions.reduce((count, question) => 
        count + (answersData[question.id] === true ? 1 : 0), 0);
      return { ...acc, [group.id]: yesCount };
    }, {});

    // 最大カウントを持つグループを特定
    const maxCount = Math.max(...Object.values(groupCounts));
    if (maxCount === 0) return 'none';

    // 最大回答数を持つグループを取得
    const maxGroups = Object.entries(groupCounts)
      .filter(([_, count]) => count === maxCount)
      .map(([id]) => id)
      .sort();

    // 結果キーの生成
    return maxGroups.length > 1 ? maxGroups.join('+') : maxGroups[0];
  };


  // 結果の処理（メッセージ表示またはリダイレクト）
  const handleResult = useCallback((resultKey) => {
    const resultData = resultMessages.find(item => item.id === resultKey);
    if (!resultData) return;

    console.log('Handling result:', resultKey, resultData);

    // 選択内容をPHPに送信する関数
    const sendAnswersToPHP = async () => {
      try {
        const response = await fetch(diagnosisAppData.diagnosisTestUrl, {  // PHPスクリプトのURL
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ answers: userAnswers })  // 選択内容をJSON形式で送信
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('PHPからの応答:', data);

        // 必要に応じて、応答に基づいて処理を行う
        if (data.status === 'success') {
          // 成功時の処理
          console.log('データ送信成功');
        } else {
          // エラー時の処理
          console.error('データ送信エラー:', data.message);
          localStorage.setItem('phpError', data.message); // エラーをローカルストレージに保存
        }
      } catch (error) {
        console.error('データ送信中にエラーが発生しました:', error);
        localStorage.setItem('phpError', error.message); // エラーをローカルストレージに保存
      }
    };

    // リダイレクトまたはメッセージ表示の前にデータを送信
    sendAnswersToPHP();

    if (resultData.redirect) {
      // リダイレクトの場合
      const basePath = window.location.origin;
      const redirectUrl = `${basePath}/${resultData.redirect}`;
      window.location.href = redirectUrl;
    } else if (resultData.message) {
      // メッセージ表示の場合
      setDisplayMessage(resultData.message);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [resultMessages, userAnswers]);


  // 結果の計算
  // const calculateResult = useCallback(() => {
  //   if (!questions.length) return;

  //   // デバッグ用に現在の回答状態を出力
  //   console.log('Current answers state:', userAnswers);

  //   // 各グループのYes回答をカウント
  //   const groupCounts = questions.reduce((acc, group) => {
  //     // 各グループの処理開始をログ
  //     console.log(`\nProcessing ${group.name} (${group.id}):`);

  //     const yesCount = group.questions.reduce((count, question) => {
  //       // nullとundefinedのチェックを追加
  //       const answer = userAnswers[question.id];
  //       const isYes = answer === true;

  //       // 各質問の状態を詳細にログ
  //       console.log(`Question ${question.id}:`, {
  //         answer,
  //         isYes,
  //         currentCount: count
  //       });

  //       return count + (isYes ? 1 : 0);
  //     }, 0);

  //     // グループの集計結果をログ
  //     console.log(`${group.id} total yes answers:`, yesCount);
  //     acc[group.id] = yesCount;
  //     return acc;
  //   }, {});

  //   // 集計結果を表示
  //   console.log('\nFinal group counts:', groupCounts);

  //   const maxCount = Math.max(...Object.values(groupCounts));
  //   console.log('Maximum yes count:', maxCount);

  //   if (maxCount === 0) {
  //     console.log('Setting result to "none" (no yes answers)');
  //     setResult('none');
  //     return;
  //   }

  //   // 最大回答数を持つグループを全て取得
  //   const maxGroups = Object.entries(groupCounts)
  //     .filter(([_, count]) => count === maxCount)
  //     .map(([id]) => id)
  //     .sort();

  //   console.log('Groups with max count:', maxGroups);

  //   // 結果キーの生成（複数グループの場合は+で連結）
  //   const resultKey = maxGroups.length > 1 ? maxGroups.join('+') : maxGroups[0];
  //   console.log('Final result key:', resultKey);

  //   setResult(resultKey);
  // }, [questions, userAnswers]);

  // 診断のリセット
  const resetDiagnosis = useCallback(() => {
    setUserAnswers({});
    setCurrentGroupIndex(0);
    setDisplayMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 各質問の回答状態を確認するユーティリティ関数
  // const validateAnswers = useCallback(() => {
  //   questions.forEach(group => {
  //     console.log(`\nValidating ${group.name} (${group.id}):`);
  //     group.questions.forEach(question => {
  //       const answer = userAnswers[question.id];
  //       console.log(`${question.id}:`, {
  //         value: answer,
  //         type: typeof answer,
  //         isBoolean: typeof answer === 'boolean'
  //       });
  //     });
  //   });
  // }, [questions, userAnswers]);

  // 現在のグループの質問を全て回答済みかチェック
  const isCurrentGroupComplete = useCallback(() => {
    if (!questions.length || currentGroupIndex >= questions.length) return false;
    const currentGroup = questions[currentGroupIndex];
    return currentGroup.questions.every(
      question => userAnswers[question.id] !== undefined
    );
  }, [questions, currentGroupIndex, userAnswers]);

  // 状態変更時の検証を追加
  // useEffect(() => {
  //   if (Object.keys(userAnswers).length > 0) {
  //     console.log('\nValidating answers after state update:');
  //     validateAnswers();
  //   }
  // }, [userAnswers, validateAnswers]);

  // ローディング表示
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-lg text-gray-600">読み込み中...</div>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-red-600 text-center">
          <p className="text-lg font-bold mb-2">エラーが発生しました</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // データが空の場合
  if (!questions.length) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-gray-600 text-center">
          質問データが見つかりません
        </div>
      </div>
    );
  }

  // 結果表示
  if (displayMessage) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 text-center">診断結果</h2>
        <div
          className="mb-8 p-6 bg-gray-50 rounded-lg leading-relaxed"
          dangerouslySetInnerHTML={{ __html: displayMessage || '結果が見つかりません' }}
        />
        <div className="text-center">
          <button
            onClick={resetDiagnosis}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200"
          >
            もう一度診断する
          </button>
        </div>
      </div>
    );
  }

  const currentGroup = questions[currentGroupIndex];

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
    <div className="mb-8">
      <h2 className="text-2xl font-bold mb-2 text-gray-800">{currentGroup.name}</h2>
      <div className="h-2 w-full bg-gray-200 rounded-full">
        <div 
          className="h-2 bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentGroupIndex + 1) / questions.length) * 100}%` }}
        />
      </div>
    </div>
    
    <div className="space-y-6">
      {currentGroup.questions.map(question => (
        <div key={question.id} className="p-5 bg-gray-50 rounded-lg">
          <p className="mb-4 text-gray-700 leading-relaxed">{question.question}</p>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleAnswer(question.id, true)}
              className={`flex-1 py-3 px-6 rounded-lg border transition-colors duration-200 ${
                userAnswers[question.id] === true
                ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                : 'bg-white border-gray-300 text-gray-700 hover:text-blue-500 hover:bg-gray-50 hover:border-blue-300'
            }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleAnswer(question.id, false)}
              className={`flex-1 py-3 px-6 rounded-lg border transition-colors duration-200 ${
                userAnswers[question.id] === false
                  ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:text-blue-500 hover:bg-gray-50 hover:border-blue-300'
              }`}
            >
              No
            </button>
          </div>
        </div>
      ))}
    </div>

    <div className="mt-8">
      {isCurrentGroupComplete() && (
        <button
          onClick={nextGroup}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
        >
          {currentGroupIndex < questions.length - 1 ? '次の質問へ' : '診断結果を見る'}
        </button>
      )}
      <div className="mt-4 text-center text-gray-500">
        {currentGroupIndex + 1} / {questions.length}ページ
      </div>
    </div>
  </div>
  );
};

export default DiagnosisApp;